import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import axios from "axios";
import {
    doc,
    updateDoc,
    collection,
    getDocs,
    query,
    where,
    serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import countryData from "../data/country.json";

const PackageTableEdit = ({ pkg, setPkg, fetchPackages, countries = [] }) => {
    const [formData, setFormData] = useState({
        countryCode: "",
        operatorId: "",
        recipientAmount: "",
        recipientCurrency: "",
        basePriceGbp: "",
        type: "airtime",
        enabled: true,
        exchange: "Reloadly",
        note: "",
    });
    const [operators, setOperators] = useState([]);
    const [showExchangeBy, setShowExchangeBy] = useState(false);
    const [globalExchangeData, setGlobalExchangeData] = useState(null);
    const [selectedCountryData, setSelectedCountryData] = useState(null);
    const [loadingOperators, setLoadingOperators] = useState(false);
    const [saving, setSaving] = useState(false);

    // ── Auto-generated display name ──
    const displayName =
        formData.recipientAmount && formData.recipientCurrency
            ? `${formData.recipientAmount} ${formData.recipientCurrency.toUpperCase()}`
            : "";

    // ── Populate form when package is selected for editing ──
    useEffect(() => {
        if (pkg) {
            setFormData({
                countryCode: pkg.countryCode || "",
                operatorId: pkg.operatorId || "",
                recipientAmount: pkg.recipientAmount?.toString() || "",
                recipientCurrency:
                    pkg.recipientCurrency?.toUpperCase() || "",
                basePriceGbp: pkg.basePriceGbp?.toString() || "",
                type: pkg.type || "airtime",
                enabled: pkg.enabled ?? true,
                exchange: pkg.exchange
                    ? pkg.exchange.charAt(0).toUpperCase() +
                      pkg.exchange.slice(1)
                    : "Reloadly",
                note: pkg.note || "",
            });

            if (pkg.countryCode) {
                const country = countries.find(
                    (c) => c.code === pkg.countryCode
                );
                setSelectedCountryData(country || null);
                fetchOperators(pkg.countryCode);
                checkGlobalExchange(pkg.countryCode);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pkg]);

    const handleClose = () => {
        setPkg(null);
        setSelectedCountryData(null);
        setOperators([]);
        setLoadingOperators(false);
        setShowExchangeBy(false);
        setGlobalExchangeData(null);
    };

    // ── Resolve country name from code ──
    const getCountryName = (code) => {
        const country = countries.find((c) => c.code === code);
        return country ? country.name : code || "-";
    };

    // ── Check global exchange for this country ──
    const checkGlobalExchange = async (countryCode) => {
        try {
            const q = query(
                collection(db, "globalExchange"),
                where("isoCode", "==", countryCode)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                setGlobalExchangeData(snapshot.docs[0].data());
                setShowExchangeBy(true);
                return true;
            } else {
                setGlobalExchangeData(null);
                setShowExchangeBy(false);
                return false;
            }
        } catch (error) {
            console.error("Error checking global exchange:", error);
            setShowExchangeBy(false);
            return false;
        }
    };

    // ── Fetch operators (existing API logic preserved) ──
    const fetchOperators = async (countryCode) => {
        try {
            setLoadingOperators(true);
            const user = auth.currentUser;
            if (!user) {
                setLoadingOperators(false);
                return;
            }
            const idToken = await user.getIdToken();
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/fetchOperators/${countryCode}?size=200`,
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.data.success) {
                setOperators(response.data.data || []);
            } else {
                console.error(
                    "Failed to fetch operators:",
                    response.data.error
                );
                setOperators([]);
            }
        } catch (error) {
            console.error("Error fetching operators:", error);
            setOperators([]);
        } finally {
            setLoadingOperators(false);
        }
    };

    // ── Fetch FX rate from Reloadly (existing API logic preserved) ──
    const fetchFxRate = async (operatorId, amount) => {
        try {
            const user = auth.currentUser;
            if (!user) return;
            const idToken = await user.getIdToken();
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/fetchFxRate`,
                {
                    operatorId: parseInt(operatorId),
                    amount: parseFloat(amount),
                    currencyCode: "GBP",
                },
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            if (response.data.success) {
                const { fxRate, currencyCode } = response.data.data;
                setFormData((prev) => ({
                    ...prev,
                    recipientAmount: fxRate.toString(),
                    recipientCurrency: currencyCode,
                }));
            } else {
                console.error(
                    "Failed to fetch FX rate:",
                    response.data.error
                );
            }
        } catch (error) {
            console.error("Error fetching FX rate:", error);
        }
    };

    // ── Calculate custom FX rate from global exchange data ──
    const calculateCustomFxRate = (basePriceGbp) => {
        if (
            globalExchangeData &&
            globalExchangeData.fxAmount &&
            selectedCountryData
        ) {
            const customRate =
                parseFloat(basePriceGbp) *
                parseFloat(globalExchangeData.fxAmount);
            const countryInfo = countryData.find(
                (c) => c.isoCode === selectedCountryData.code
            );
            setFormData((prev) => ({
                ...prev,
                recipientAmount: customRate.toFixed(2),
                recipientCurrency:
                    countryInfo?.currency || prev.recipientCurrency,
            }));
        }
    };

    // ── Handle form field changes ──
    const handleChange = async (e) => {
        const { name, value, type, checked } = e.target;

        // Handle enabled toggle
        if (type === "checkbox" && name === "enabled") {
            setFormData((prev) => ({ ...prev, enabled: checked }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));

        // Base price changed → auto-calculate recipient amount
        if (name === "basePriceGbp") {
            let price = parseFloat(value);
            if (isNaN(price)) price = 0;
            if (price < 0) price = 0;

            setFormData((prev) => ({
                ...prev,
                basePriceGbp: price.toString(),
            }));

            const { operatorId, exchange } = formData;
            if (operatorId && exchange === "Reloadly") {
                fetchFxRate(operatorId, price);
            } else if (exchange === "Custom" && globalExchangeData) {
                calculateCustomFxRate(price);
            }
            return;
        }

        // Operator changed → recalculate if base price exists
        if (name === "operatorId" && formData.basePriceGbp) {
            if (formData.exchange === "Reloadly") {
                fetchFxRate(value, formData.basePriceGbp);
            } else if (
                formData.exchange === "Custom" &&
                globalExchangeData
            ) {
                calculateCustomFxRate(formData.basePriceGbp);
            }
        }

        // Exchange type changed → recalculate
        if (name === "exchange") {
            if (
                value === "Reloadly" &&
                formData.operatorId &&
                formData.basePriceGbp
            ) {
                fetchFxRate(formData.operatorId, formData.basePriceGbp);
            } else if (
                value === "Custom" &&
                globalExchangeData &&
                formData.basePriceGbp
            ) {
                calculateCustomFxRate(formData.basePriceGbp);
            }
        }
    };

    // ── Save handler ──
    const handleSave = async () => {
        if (!pkg?.packageId) return;

        // Validation
        const recipientAmt = parseFloat(formData.recipientAmount);
        const basePrice = parseFloat(formData.basePriceGbp);

        if (isNaN(recipientAmt) || recipientAmt <= 0) {
            return alert("Recipient Amount must be greater than 0.");
        }
        if (!formData.recipientCurrency.trim()) {
            return alert("Recipient Currency is required.");
        }
        if (isNaN(basePrice) || basePrice <= 0) {
            return alert("Base Price (GBP) must be greater than 0.");
        }

        try {
            setSaving(true);
            const operator = operators.find(
                (op) => op.id.toString() === formData.operatorId
            );
            const generatedDisplayName = `${recipientAmt} ${formData.recipientCurrency.toUpperCase()}`;

            await updateDoc(doc(db, "packages", pkg.packageId), {
                operatorId: parseInt(formData.operatorId, 10),
                operatorName: operator?.name || pkg.operatorName || "",
                displayName: generatedDisplayName,
                recipientAmount: recipientAmt,
                recipientCurrency:
                    formData.recipientCurrency.toUpperCase(),
                basePriceGbp: basePrice,
                type: formData.type,
                enabled: formData.enabled,
                exchange: formData.exchange.toLowerCase(),
                note: formData.note,
                updatedAt: serverTimestamp(),
            });

            if (fetchPackages) fetchPackages();
            handleClose();
        } catch (error) {
            console.error("Error updating package:", error);
            alert("❌ Failed to update package.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={!!pkg} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Edit Package</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {/* ── Country (read-only) ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                            value={getCountryName(formData.countryCode)}
                            disabled
                        />
                    </Form.Group>

                    {/* ── Operator ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Operator</Form.Label>
                        <Form.Select
                            name="operatorId"
                            value={formData.operatorId}
                            onChange={handleChange}
                            disabled={loadingOperators}
                            required
                        >
                            <option value="">
                                {loadingOperators
                                    ? "Loading operators..."
                                    : "Select Operator"}
                            </option>
                            {operators.map((op) => (
                                <option key={op.id} value={op.id}>
                                    {op.name}
                                </option>
                            ))}
                        </Form.Select>
                        {loadingOperators && (
                            <div className="text-muted small mt-1">
                                <span
                                    className="admin-loader-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Fetching operators...
                            </div>
                        )}
                    </Form.Group>

                    {/* ── Exchange By (only if global exchange exists) ── */}
                    {showExchangeBy && (
                        <Form.Group className="mb-2">
                            <Form.Label>Exchange By</Form.Label>
                            <div className="d-flex gap-3">
                                {["Reloadly", "Custom"].map((exchange) => (
                                    <div
                                        className="form-check"
                                        key={exchange}
                                    >
                                        <input
                                            type="radio"
                                            className="form-check-input"
                                            name="exchange"
                                            value={exchange}
                                            id={`edit-exchange-${exchange}`}
                                            checked={
                                                formData.exchange ===
                                                exchange
                                            }
                                            onChange={handleChange}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={`edit-exchange-${exchange}`}
                                        >
                                            {exchange}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        </Form.Group>
                    )}

                    {/* ── Base Price (GBP) ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Base Price (GBP)</Form.Label>
                        <Form.Control
                            type="number"
                            name="basePriceGbp"
                            value={formData.basePriceGbp}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            placeholder="Enter base price in GBP"
                            required
                        />
                    </Form.Group>

                    {/* ── Recipient Amount ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>
                            Recipient Amount{" "}
                            {formData.recipientCurrency &&
                                `(${formData.recipientCurrency.toUpperCase()})`}
                        </Form.Label>
                        <Form.Control
                            type="number"
                            name="recipientAmount"
                            value={formData.recipientAmount}
                            onChange={handleChange}
                            step="0.01"
                            min="0"
                            placeholder="Enter recipient amount"
                            required
                        />
                        {formData.exchange === "Reloadly" &&
                            formData.recipientAmount &&
                            formData.operatorId && (
                                <div className="text-info small mt-1 fst-italic">
                                    ✨ Auto-calculated from Reloadly FX rate
                                </div>
                            )}
                        {formData.exchange === "Custom" &&
                            formData.recipientAmount &&
                            globalExchangeData && (
                                <div className="text-info small mt-1 fst-italic">
                                    ✨ Auto-calculated from Custom exchange
                                    rate
                                </div>
                            )}
                    </Form.Group>

                    {/* ── Recipient Currency ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Recipient Currency</Form.Label>
                        <Form.Control
                            type="text"
                            name="recipientCurrency"
                            value={formData.recipientCurrency}
                            onChange={handleChange}
                            placeholder="e.g. AFN, BDT, PKR"
                            required
                        />
                        <Form.Text className="text-muted">
                            This is the currency the recipient receives.
                        </Form.Text>
                    </Form.Group>

                    {/* ── Type ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                        >
                            <option value="airtime">Airtime</option>
                            <option value="data">Data</option>
                        </Form.Select>
                    </Form.Group>

                    {/* ── Enabled Toggle ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Status</Form.Label>
                        <div className="d-flex align-items-center gap-2">
                            <div className="form-check form-switch mb-0">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    name="enabled"
                                    checked={formData.enabled}
                                    onChange={handleChange}
                                    style={{
                                        width: "2.5em",
                                        height: "1.25em",
                                    }}
                                />
                            </div>
                            <span
                                className={`fw-semibold ${
                                    formData.enabled
                                        ? "text-success"
                                        : "text-danger"
                                }`}
                            >
                                {formData.enabled ? "Enabled" : "Disabled"}
                            </span>
                        </div>
                    </Form.Group>

                    {/* ── Note ── */}
                    <Form.Group className="mb-2">
                        <Form.Label>Note</Form.Label>
                        <Form.Control
                            as="textarea"
                            name="note"
                            value={formData.note}
                            onChange={handleChange}
                            rows={2}
                            placeholder="Optional note"
                        />
                    </Form.Group>

                    {/* ── Display Name Preview ── */}
                    {displayName && (
                        <div
                            className="mb-2 p-2 rounded"
                            style={{
                                backgroundColor: "rgba(26, 115, 232, 0.1)",
                                border: "1px dashed #1A73E8",
                            }}
                        >
                            <small className="text-muted d-block">
                                Display Name (auto-generated)
                            </small>
                            <strong className="text-primary">
                                {displayName}
                            </strong>
                        </div>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving}
                >
                    {saving ? "Updating..." : "Update"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default PackageTableEdit;